import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { ApiEndPoints, ApiMethod, ShiftSwapRequestType } from 'src/app/core/services/const';
import { HttpService } from 'src/app/core/services/http/http.service';
import { EmployeeShiftSwap } from '../models/employee-shift-swap';
import { ShiftForSwap } from '../models/shift-for-swap';
import { SwappingStatus } from '../models/swapping-status';
import { swapRequestGroups } from '../util/my-swap-requests';

// ============================================================================
// BUG-TRAIL — ShiftSwap-Frontend ↔ Backend-Drift (Stand 2026-05-12)
// ============================================================================
// Im Commit `3f2fb137 fix: Update to .NET 10 SDK` (19.12.2025) wurde der
// `ShiftSwapController` von 249 auf 135 Zeilen reduziert und vollständig auf
// das neue `IShiftSwapService`-Pattern umgestellt. Die alten MediatR-Endpoints
// (`PutShiftForSwap`, `AddTargetedRequestToSwapRequest`, `EmployeeAcceptShiftSwapCommand`,
// `GetEmployeeSwapShiftsList`, `GetShiftsForSwapQuery`, `GetAllEmplyeesSwappingProcessQuery`,
// `GetPotentialShiftSwappersQuery`, `GetShiftSwapStatusQuery`, `GetEmployeeWorkShiftsForSwapingQuery`,
// `AddProposedOptionToSwapRequestCommand`, `GetShiftsSwapCandidateQuery`,
// `SwapEmployeeBetweenShiftsCommand`, `SwapEmployeeBetweenTargetedShiftsCommand`,
// `CancelShiftSwapRequest`, `SelectCandidateForOpenSwap`, `GetAdminShiftSwapDetails`,
// `GetPendingAdminApprovals`, `AdminApproveShiftSwap`, `AdminRejectShiftSwap`)
// wurden ersatzlos entfernt — das Frontend wurde NICHT nachgezogen.
//
// Neue Backend-API (8 Endpoints in `ShiftSwapController.cs`):
//   POST /api/ShiftSwap/RequestSwap          (Drop/Swap/Pickup-Initiierung)
//   POST /api/ShiftSwap/Accept/{id}          (Target akzeptiert)
//   POST /api/ShiftSwap/Reject/{id}          (Target lehnt ab — oder Requester cancelt?)
//   POST /api/ShiftSwap/ManagerApprove/{id}  (Manager approve/reject via IsApproved-Bool)
//   GET  /api/ShiftSwap/GetPending
//   GET  /api/ShiftSwap/GetHistory?includeAll=true|false
//   GET  /api/ShiftSwap/{id}
//   GET  /api/ShiftSwap/GetForManager?departmentId=
//
// DTOs: `Application/ShiftSwap/DTOs/ShiftSwapDtos.cs` (PascalCase Success-Path).
// HttpService liefert das volle `ApiResponse<T>` zurück — `.Data` muss
// explizit ausgepackt werden (siehe `mapToEmployeeShiftSwap` unten).
//
// MIGRATIONSPLAN: `C:\Users\dawad\.claude\plans\structured-seeking-moler.md`
//
// PHASEN-STATUS:
//   [✅ Phase 1, 2026-05-12] GetEmployeeSwapShiftsList + GetShiftsForSwapQuery
//                             auf /GetHistory umgebogen, minimaler Mapper. Damit
//                             stoppen die 400er beim Login der Mobile-App.
//   [✅ Phase 2, 2026-08-31] ERLEDIGT — aber NICHT als `admin-swap-approval.service.ts`:
//               Der Name war irreführend („Admin" ist im Backend eine andere Rolle als
//               „Manager"). Gebaut als `shift-swap-approval.service.ts` + Genehmigungs-Liste,
//               erreichbar als Pop-up im Planer (Toolbar-Knopf, Hausmuster der
//               Bewerbungs-Inbox). Die zusaetzliche Route `/shift-swap-approvals` ist am
//               14.09.2026 entfallen — eine Liste, zwei Oberflaechen; der Zaehler sitzt
//               seitdem am Planer-Knopf.
//               ⚠️ `RequireManagerRole` = `RequireRole(MANAGER, ADMIN)` — KEIN TEAMLEADER.
//   [✅ Phase 3, 2026-08-31] VIER von fünf Pfaden migriert:
//                 EmployeeAcceptShiftSwapCommand → /Accept/{id}
//                 CancelShiftSwapRequest         → /Reject/{id}
//                 GetShiftSwapStatusObservable   → /{id}
//                 PutShiftForSwap                → /RequestSwap (Drop)  ← neu
//               Beide Backend-Risiken sind geklärt:
//                 (a) JA — RejectSwapAsync prüft nur den Status (Created/AcceptedFromEmployee)
//                     und nicht, WER ablehnt; der Self-Cancel des Initiators geht durch.
//                     Nachwirkung: Der Vorgang endet als `Rejected`, nicht als `Cancelled`.
//                 (b) NEIN, und das war ein Fehler: ManagerApprove hat `IsApproved` gar nicht
//                     gelesen und jede Ablehnung als Genehmigung ausgeführt. Im Backend
//                     behoben am 31.08.2026 (Tests: Audit=B2-1). Erst dadurch ist Phase 2
//                     überhaupt gefahrlos möglich.
//               Die Blockade „das Frontend führt nur die EventId (Guid)" ist aufgelöst: Das
//               Backend liefert die numerische Id seit dem 31.08.2026 als
//               `CalenderEvent.WorkShiftId` mit (zuvor stand sie im Handler, wurde aber nicht
//               ausgeliefert — und `id` im Event trägt die EmployeeId, nicht die Schicht-Id).
//               Damit war der Drop-Pfad migrierbar, und der Tausch ist erstmals anstossbar.
//
//               NICHT migriert bleibt der DIREKTTAUSCH mit einer bestimmten Person:
//                 AddTargetedRequestToSwapRequest → /RequestSwap (Swap)
//               Nicht wegen der Id, sondern wegen der Auswahlliste: Sie stammt aus
//               GetPotentialShiftSwappersList, und dieser Endpunkt hat im neuen Backend KEIN
//               Gegenstück (Phase 4). Der Dialog bekäme also ohnehin keine Kandidaten. Wer den
//               Direkttausch will, braucht zuerst einen neuen Backend-Endpunkt für die
//               Kandidatenliste — und dann zusätzlich TargetEmployeeId + OfferedWorkShiftId.
//   [⏳ Phase 4] Marketplace-Sections (SHIFTS_AVAILABLE_FOR_SWAP, admin-show-shifts-in-swapping-process,
//               show-shift-for-swap, GetPotentialShiftSwappersList, GetAllEmplyeesSwappingProcessQuery,
//               GetShiftsSwapCandidateQuery, SwapEmployeeBetweenShifts*Command, SelectCandidateForOpenSwap)
//               haben KEIN Backend-Equivalent mehr — UI muss entfernt oder Backend muss neue Endpoints
//               liefern (Stakeholder-Klärung).
//   [◐ Phase 5, 2026-08-31] Specs für alle migrierten Pfade:
//               `shift-swap-api-migration.vitest.spec.ts`  — Accept, Reject(Cancel), GetById
//               `shift-swap-reject.vitest.spec.ts`         — Ablehnen durch den Angefragten
//               `shift-swap-drop.vitest.spec.ts`           — Schicht abgeben (Drop)
//               `shift-swap-approval.service.vitest.spec.ts` — Manager-Genehmigung
//               `swap-status-mapping.vitest.spec.ts`       — Statusabbildung 1–8
//               `gate.vitest.spec.ts`                      — Sichtbarkeit des Einstiegs
//               Jeder Fix ist per Gegenprobe belegt (Fix zurücknehmen ⇒ Test rot).
//               Ohne Testabdeckung bleiben die Phase-4-Marketplace-Flächen.
//
// Methoden, die in diesem Service noch auf tote Endpoints zeigen, sind unten
// einzeln mit `@deprecated DEAD-ENDPOINT` markiert.
// ============================================================================
@Injectable({
  providedIn: 'root',
})
export class ShiftSwapService {
  /**
   * Meine **offenen** Tauschanfragen — das, was in der Kachel „Das erwartet dich heute" steht.
   *
   * ⚠️ **Der Endpunkt dahinter ist eine Historie.** `GET /ShiftSwap/GetHistory` liefert auch
   * abgelehnte, angenommene und abgebrochene Vorgänge; bis 09/2026 landeten sie alle in dieser
   * Liste. Gemessen am 19.09.2026 (Mitarbeiter 44): **fünf Einträge, alle erledigt** — vier
   * abgelehnt, einer vom Admin angenommen. Die Aufteilung macht
   * [`util/my-swap-requests.ts`](../util/my-swap-requests.ts) an **einer** Stelle, damit die
   * drei Konsumenten (Desktop-Startseite, Telefon-Tab, Listenkomponente) nicht drei Regeln
   * führen.
   */
  private employeeSwapShiftListSource: BehaviorSubject<EmployeeShiftSwap[]> =
    new BehaviorSubject([] as EmployeeShiftSwap[]);
  employeeSwapShiftList = this.employeeSwapShiftListSource.asObservable();

  /**
   * Offen, aber über die Frist oder den Schichtbeginn hinaus.
   *
   * Bewusst ein **eigener** Strom statt eines Filters beim Verbraucher: eine zwei Wochen alte
   * unbeantwortete Anfrage soll nicht verschwinden, sondern in einem eigenen Band stehen.
   * Wegfiltern wäre der bequeme Fehler.
   */
  private employeeOverdueSwapListSource: BehaviorSubject<EmployeeShiftSwap[]> =
    new BehaviorSubject([] as EmployeeShiftSwap[]);
  employeeOverdueSwapList = this.employeeOverdueSwapListSource.asObservable();

  private swappingStatusSource: BehaviorSubject<SwappingStatus> =
    new BehaviorSubject(new SwappingStatus());
  swappingStatus = this.swappingStatusSource.asObservable();

  private employeeShiftSwappingProcessListSource: BehaviorSubject<
    EmployeeShiftSwap[]
  > = new BehaviorSubject([] as EmployeeShiftSwap[]);
  employeeSwapProcessShiftList =
    this.employeeShiftSwappingProcessListSource.asObservable();

  private potentialShiftSwappersListSource: BehaviorSubject<ShiftForSwap[]> =
    new BehaviorSubject([] as ShiftForSwap[]);
  potentialShiftSwappersList =
    this.potentialShiftSwappersListSource.asObservable();
  private shiftForSwapListSource: BehaviorSubject<EmployeeShiftSwap[]> =
    new BehaviorSubject([] as EmployeeShiftSwap[]);
  shiftForSwapList = this.shiftForSwapListSource.asObservable();

  private shiftForPossibleWorkShiftListSource: BehaviorSubject<ShiftForSwap[]> =
    new BehaviorSubject([] as ShiftForSwap[]);
  shiftForPossibleWorkShift =
    this.shiftForPossibleWorkShiftListSource.asObservable();

  private shiftsSwapCandidateListSource: BehaviorSubject<ShiftForSwap[]> =
    new BehaviorSubject([] as ShiftForSwap[]);
  shiftsSwapCandidate = this.shiftsSwapCandidateListSource.asObservable();

  constructor(
    private httpClient: HttpService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  /**
   * Ermittelt die **numerische** Schicht-Id zu einer `eventId` (Guid) — der zweite Weg zur
   * `WorkShiftId`, wenn sie nicht im Kalender-Event steht.
   *
   * Warum es diesen zweiten Weg braucht: Die Swap-API verlangt die numerische Id. Das
   * Kalender-Event liefert sie seit dem Server-Commit `bcb17347` als `workShiftId` mit — aber nur
   * dort, wo dieser Commit auch läuft. Bis er auf Test und Live ausgerollt ist, käme der ganze
   * Tausch-Einstieg dort nicht zustande, obwohl das Frontend fertig ist. `GetWorkShiftDetails`
   * führt `WorkShiftId` seit Langem und ist auch für Mitarbeiter freigegeben (am 31.08.2026 gegen
   * das lokale Backend nachgemessen). Ein zusätzlicher Aufruf ist der Preis dafür, dass der
   * Einstieg nicht am Deploy-Zeitpunkt des Servers hängt.
   *
   * ⚠️ Nicht `event.id` als Ersatz nehmen: Das Feld trägt im Kalender-Event die
   * **Mitarbeiter**-Id (`GetEventsForCompanyQueryHandler`: `Id = ws.EmployeeId`). Wer es
   * verwechselt, stellt Tauschanfragen auf fremde Schichten.
   *
   * Liefert `0`, wenn nichts zu ermitteln ist — der Aufrufer darf dann **nicht** senden.
   */
  ResolveWorkShiftId(eventId: string): Observable<number> {
    if (!eventId) {
      return of(0);
    }
    return this.httpClient
      .requestCall(
        ApiEndPoints.GetWorkShiftDetails + '/' + eventId,
        ApiMethod.GET
      )
      .pipe(
        map((res: any) => {
          const details = this.unwrap<any>(res) ?? res;
          return Number(details?.WorkShiftId ?? details?.workShiftId ?? 0) || 0;
        }),
        catchError(() => of(0))
      );
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 3 Migration ausstehend).
   * Ziel: `POST /api/ShiftSwap/RequestSwap` mit
   *   `{ WorkShiftId: eventId, RequestType: ShiftSwapRequestType.Drop }`.
   * Aktuell wirft das Backend 404 — der alte `/PutShiftForSwap`-Endpoint existiert
   * seit dem .NET 10 Refactor (19.12.2025) nicht mehr.
   */
  PutShiftForSwap(eventId): Observable<any> {
    return this.httpClient
      .requestCall(ApiEndPoints.PutShiftForSwap, ApiMethod.PUT, {
        EventId: eventId,
      })
      .pipe(
        tap((details: any) => {
          this.GetEmployeeSwapShiftsList();
        })
      );
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 3).
   * Ziel: `POST /api/ShiftSwap/RequestSwap` mit
   *   `{ WorkShiftId: employeeEventId, OfferedWorkShiftId: targetedEventId,
   *      TargetEmployeeId: <vom Target-Shift abgeleitet>, RequestType: Swap }`.
   * OFFEN: `TargetEmployeeId` muss vorher aus Shift-Daten ermittelt werden —
   * der alte Backend-Pfad hat das implizit über `TargetedWorkShiftId` gemacht.
   */
  AddTargetedRequestToSwapRequest(
    employeeEventId,
    targetedEventId
  ): void {
    this.httpClient
      .requestCall(
        ApiEndPoints.AddTargetedRequestToSwapRequest,
        ApiMethod.POST,
        {
          WorkShiftEventId: employeeEventId,
          TargetedWorkShiftId: targetedEventId,
        }
      )
      .subscribe((details: any) => {
        if (details > 0) {
          this.toastr.success(
            this.translate.instant(
              'YOUR SHIFT SWAP REQUEST HAS BEEN SUCCESSFULLY REGISTERED'
            ),
            this.translate.instant('SWAP REQUEST SUCCESSFUL')
          );
          this.GetEmployeeSwapShiftsList();
        }
      });
  }

  GetEmployeeSwapShiftsList(): void {
    this.loadShiftSwapHistory().subscribe();
  }

  GetEmployeeSwapShiftsListAsync(): Observable<EmployeeShiftSwap[]> {
    return this.loadShiftSwapHistory();
  }

  /**
   * **Meine** Tauschvorgänge — `GET /ShiftSwap/GetHistory?includeAll=false`: alles, woran der
   * angemeldete Mitarbeiter beteiligt ist, als Antragsteller **oder** als Ziel.
   *
   * ⚠️ Diese Methode befüllte bis zum 31.08.2026 **beide** Ströme, auch
   * `shiftForSwapListSource` („Tauschangebote von Kollegen"). Der Kommentar nannte das eine
   * Einsparung eines HTTP-Calls; tatsächlich war es eine Verwechslung zweier Fachbegriffe. Die
   * Kachel „Tauschangebote" zeigte damit die eigenen Anfragen, und ein **fremdes** Angebot
   * erschien nirgends — niemand konnte einen Tausch annehmen, obwohl `GetPending` genau dafür
   * bereitsteht. Getrennt: {@link loadPendingSwapOffers}.
   */
  private loadShiftSwapHistory(): Observable<EmployeeShiftSwap[]> {
    return this.httpClient
      .requestCall(
        ApiEndPoints.GetEmployeeSwapShiftsList,
        ApiMethod.GET,
        undefined,
        new HttpParams().set('includeAll', 'false')
      )
      .pipe(
        map((res: any) => this.mapToEmployeeShiftSwap(res?.Data ?? res?.data ?? res)),
        tap((items) => {
          const gruppen = swapRequestGroups(items);
          this.employeeSwapShiftListSource.next(gruppen.offen);
          this.employeeOverdueSwapListSource.next(gruppen.ueberfaellig);
        })
      );
  }

  /**
   * **Fremde** Angebote, die ich übernehmen könnte — `GET /ShiftSwap/GetPending`.
   *
   * Das Backend filtert selbst: Status `Created`, nicht vom Anfragenden selbst, passende
   * Abteilung und Qualifikation, `CanBeAccepted`. Das Frontend zeigt die Liste unverändert an;
   * jede eigene Nachfilterung würde eine Server-Regel verdoppeln, die sich ändern kann.
   */
  private loadPendingSwapOffers(): Observable<EmployeeShiftSwap[]> {
    return this.httpClient
      .requestCall(ApiEndPoints.GetPendingSwapOffers, ApiMethod.GET)
      .pipe(
        map((res: any) => this.mapToEmployeeShiftSwap(res?.Data ?? res?.data ?? res)),
        tap((items) => this.shiftForSwapListSource.next(items))
      );
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 3).
   * Ziel: `GET /api/ShiftSwap/{swapingId}` → `ApiResponse<ShiftSwapResponseDto>`.
   * Body-Casing wechselt von `SwappingStatus` (Bare-DTO) auf `ApiResponse.Data`
   * (`.Data` muss ausgepackt werden, siehe `shift-swap.service.ts`-Header).
   */
  GetShiftSwapStatus(swapingId: number): void {
    this.httpClient
      .requestCall(
        ApiEndPoints.GetSwapById + '/' + swapingId,
        ApiMethod.GET
      )
      .subscribe((details: SwappingStatus) => {
        this.swappingStatusSource.next(details);
      });
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 4 — Marketplace-Feature im Backend entfernt).
   * Kein direkter Equivalent in der neuen API. Nur Admin-Sichten (siehe
   * `admin-show-shifts-in-swapping-process.component.ts`). Wenn das Feature
   * weiter gebraucht wird, neues Backend-Endpoint nötig (Stakeholder-Klärung).
   */
  GetAllEmplyeesSwappingProcessQuery(): void {
    this.httpClient
      .requestCall(
        ApiEndPoints.GetAllEmplyeesSwappingProcessQuery,
        ApiMethod.GET
      )
      .subscribe((details: EmployeeShiftSwap[]) => {
        this.employeeShiftSwappingProcessListSource.next(details);
      });
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 4 — Marketplace-Feature entfernt).
   * Kein Equivalent. Aufrufende UI-Komponenten müssen entfernt werden.
   */
  GetPotentialShiftSwappersList(eventId: string): void {
    this.httpClient
      .requestCall(
        ApiEndPoints.GetPotentialShiftSwappersQuery + '/' + eventId,
        ApiMethod.GET
      )
      .subscribe((details: ShiftForSwap[]) => {
        this.potentialShiftSwappersListSource.next(details);
      });
  }

  /** Angebote von Kollegen laden — siehe {@link loadPendingSwapOffers}. */
  GetShiftsForSwapQuery(): void {
    this.loadPendingSwapOffers().subscribe();
  }

  GetShiftsForSwapQueryAsync(): Observable<EmployeeShiftSwap[]> {
    return this.loadPendingSwapOffers();
  }

  /**
   * Phase 1 (.NET 10 Backend-Refactor): das alte UI-Model `EmployeeShiftSwap` und
   * das neue Backend-DTO `ShiftSwapResponseDto` unterscheiden sich. Bis die UI
   * in Phase 3/4 nachgezogen ist, mappen wir minimal — Felder ohne Equivalent
   * (IconCode, CandidateShiftListId, RequestsShift) bekommen sichere Defaults.
   * Die UI-Bindings auf Werte wie `RequestsShift` müssen `?.`/`*ngIf`-tolerant
   * bleiben, was sie laut Bestand schon sind.
   *
   * Backend-Casing ist PascalCase (Success-Path, `PropertyNamingPolicy = null`),
   * camelCase wird trotzdem akzeptiert für etwaige Edge-Cases.
   */
  private mapToEmployeeShiftSwap(payload: unknown): EmployeeShiftSwap[] {
    if (!Array.isArray(payload)) return [];
    return payload.map((dto: any) => {
      const start = dto?.ShiftStart ?? dto?.shiftStart;
      const end = dto?.ShiftEnd ?? dto?.shiftEnd;
      return {
        Id: dto?.Id ?? dto?.id,
        EmployeeFullName: dto?.EmployeeName ?? dto?.employeeName ?? '',
        EmployeeId: dto?.EmployeeId ?? dto?.employeeId,
        StartTime: start,
        EndTime: end,
        ShiftId: dto?.WorkShiftId ?? dto?.workShiftId,
        SkillName: dto?.SkillName ?? dto?.skillName ?? '',
        IconCode: '',
        CandidateShiftListId: [],
        RequestsShift: null,
        DateString: start ? new Date(start).toLocaleDateString() : '',
        // Vier Felder, die der Server seit jeher liefert und die dieser Mapper bis
        // 09/2026 verwarf. Ohne `Status` kann die Oberflaeche einen offenen Vorgang
        // nicht von einem abgeschlossenen unterscheiden — und zeigte beide.
        Status: dto?.Status ?? dto?.status ?? null,
        RequestedAt: dto?.RequestedAt ?? dto?.requestedAt ?? null,
        ExpiresAt: dto?.ExpiresAt ?? dto?.expiresAt ?? null,
        IsExpired: dto?.IsExpired ?? dto?.isExpired ?? null,
      } as EmployeeShiftSwap;
    });
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 4 — Marketplace-Feature entfernt).
   * Wurde fürs Vorschlagen einer eigenen Schicht als Tausch-Option genutzt;
   * im neuen Workflow gibt der Initiator `OfferedWorkShiftId` direkt im
   * `RequestSwap`-Body an. Aufrufende UI muss umgebaut/entfernt werden.
   */
  GetEmployeeWorkShiftsForSwapingQuery(shittId: any, EmployeeId: any): void {
    this.httpClient
      .requestCall(
        ApiEndPoints.GetEmployeeWorkShiftsForSwapingQuery +
          '/' +
          shittId +
          '/' +
          EmployeeId,
        ApiMethod.GET
      )
      .subscribe((details: ShiftForSwap[]) => {
        this.shiftForPossibleWorkShiftListSource.next(details);
      });
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 4 — Workflow im neuen Backend entfernt).
   * Im neuen Modell gibt es kein „Vorschlagen mehrerer Optionen" mehr —
   * der Target nimmt direkt via `POST /Accept/{id}` an. Wenn das alte Multi-
   * Vorschlag-Konzept fachlich gebraucht wird, Backend-Reimplementierung nötig.
   */
  proposedOptionList(accepted: number[], swapId: number): Observable<any> {
    return this.httpClient
      .requestCall(
        ApiEndPoints.AddProposedOptionToSwapRequestCommand,
        ApiMethod.POST,
        {
          ProposedOptions: accepted,
          SwapId: swapId,
        }
      )
      .pipe(
        tap((result: any) => {
          if (result === 1) {
            this.toastr.success(
              this.translate.instant('OFFER_SUBMITTED_SUCCESSFULLY'),
              this.translate.instant('SUCCESS')
            );
            // Listen aktualisieren
            this.GetShiftsForSwapQuery();
            this.GetEmployeeSwapShiftsList();
          }
        })
      );
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 4 — Marketplace-Feature entfernt).
   * Lieferte vorher die Kandidaten-Liste passender Schichten für einen Swap;
   * im neuen Modell kein Equivalent. Aufrufende UI muss entfernt werden.
   */
  GetShiftsSwapCandidateQuery(shittId: any): void {
    this.httpClient
      .requestCall(
        ApiEndPoints.GetShiftsSwapCandidateQuery + '/' + shittId,
        ApiMethod.GET
      )
      .subscribe((details: ShiftForSwap[]) => {
        this.shiftsSwapCandidateListSource.next(details);
      });
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 4 — Admin-Force-Swap entfernt).
   * Im neuen Modell läuft Swap immer über `RequestSwap` + `Accept`/`ManagerApprove`.
   * Wenn Admin-Direkt-Tausch fachlich gebraucht wird, separate Backend-Story.
   */
  SwapEmployeeBetweenShifts(swapShiftId: any, ShiftId: any): void {
    this.httpClient
      .requestCall(
        ApiEndPoints.SwapEmployeeBetweenShiftsCommand,
        ApiMethod.PUT,
        {
          SwapShiftId: swapShiftId,
          ShiftId: ShiftId,
        }
      )
      .subscribe((details: ShiftForSwap[]) => {
        this.shiftsSwapCandidateListSource.next(details);
      });
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 4 — Admin-Force-Swap entfernt).
   * Wird im neuen Modell durch `Accept/{id}` auf einer Targeted-Swap-Anfrage
   * abgedeckt — d. h. der Employee selbst akzeptiert. Kein Admin-Endpoint mehr.
   */
  SwapEmployeeBetweenTargetedShifts(swapShiftId: any): void {
    this.httpClient
      .requestCall(
        ApiEndPoints.SwapEmployeeBetweenTargetedShiftsCommand,
        ApiMethod.PUT,
        {
          SwapShiftId: swapShiftId,
        }
      )
      .subscribe((details: number) => {
        if (details == 1) {
          this.toastr.success(
            this.translate.instant(
              'THE SHIFTS HAVE BEEN SUCCESSFULLY EXCHANGED'
            ),
            this.translate.instant('SHIFT EXCHANGE SUCCESSFUL')
          );
          this.GetAllEmplyeesSwappingProcessQuery();
        }
      });
  }

  /**
   * Packt die Nutzlast aus einer `ApiResponse<T>` aus.
   *
   * Die neue Swap-API antwortet mit `{ Data, Notifications, Success }`; `HttpService.requestCall`
   * reicht das unausgepackt durch (sein `tap` liest nur die Notifications heraus). Erfolgs-Bodies
   * sind PascalCase, Fehler-Bodies camelCase — deshalb beide Schreibweisen.
   */
  private unwrap<T>(response: any): T | null {
    if (response === null || response === undefined) {
      return null;
    }
    return (response.Data ?? response.data ?? null) as T | null;
  }

  /**
   * Erfolgskriterium der neuen API.
   *
   * Die alten Endpunkte meldeten Erfolg mit der nackten Zahl `1`; die neuen liefern das
   * angelegte bzw. geänderte Objekt in `Data`. Ein `Success: false` ist explizit ein Fehlschlag,
   * ein fehlendes `Data` ebenso — sonst würde ein leerer Treffer als Erfolg durchgehen.
   */
  private isSuccess(response: any): boolean {
    if (!response) {
      return false;
    }
    if (response.Success === false || response.success === false) {
      return false;
    }
    return this.unwrap<unknown>(response) !== null;
  }

  /**
   * Nimmt eine Tauschanfrage an — `POST /api/ShiftSwap/Accept/{id}`.
   *
   * Migriert am 31.08.2026 vom entfernten `EmployeeAcceptShiftSwapCommand` (Phase 3). Die Antwort
   * ist jetzt `ApiResponse<ShiftSwapResponseDto>` statt einer nackten `1`; der alte Vergleich
   * `result == 1` traf deshalb nie zu — der Nutzer bekam auch bei Erfolg keine Rückmeldung.
   */
  EmployeeAcceptShiftSwapCommand(swapShiftId: any): void {
    this.httpClient
      .requestCall(
        ApiEndPoints.AcceptSwap + '/' + swapShiftId,
        ApiMethod.POST,
        {
          ShiftSwapId: swapShiftId,
        }
      )
      .subscribe((result: any) => {
        if (this.isSuccess(result)) {
          this.GetEmployeeSwapShiftsList();
          this.GetShiftsForSwapQuery();
          this.toastr.success(
            this.translate.instant('EXCHANGE REQUEST ACCEPTED'),
            this.translate.instant('EXCHANGE REQUEST CONFIRMATION')
          );
        }
      });
  }

  /**
   * Liest einen Tauschvorgang — `GET /api/ShiftSwap/{id}`.
   *
   * Migriert am 31.08.2026 (Phase 3). Die Antwort ist eine `ApiResponse<ShiftSwapResponseDto>`;
   * ohne Auspacken landete bisher die ganze Hülle im `swappingStatus`-Strom, und jede daran
   * hängende Ansicht las auf einem Objekt, das ihre Felder gar nicht hat.
   */
  GetShiftSwapStatusObservable(swapingId: number): Observable<SwappingStatus> {
    return this.httpClient
      .requestCall(
        ApiEndPoints.GetSwapById + '/' + swapingId,
        ApiMethod.GET
      )
      .pipe(
        // map statt tap: Der Aufrufer (SwapStatusService.getSwapRequestDetails) mappt den
        // Rueckgabewert direkt weiter. Mit tap floesse die ungeoeffnete Huelle durch und
        // der deklarierte Typ SwappingStatus waere eine Falschaussage.
        map((response: any) => {
          const details = this.unwrap<SwappingStatus>(response);
          if (details) {
            this.swappingStatusSource.next(details);
          }
          return details as SwappingStatus;
        })
      );
  }

  /** Observable-Variante von {@link EmployeeAcceptShiftSwapCommand} — gleicher Endpunkt. */
  EmployeeAcceptShiftSwapCommandObservable(swapShiftId: any): Observable<any> {
    return this.httpClient
      .requestCall(
        ApiEndPoints.AcceptSwap + '/' + swapShiftId,
        ApiMethod.POST,
        {
          ShiftSwapId: swapShiftId,
        }
      )
      .pipe(
        tap((result: any) => {
          if (this.isSuccess(result)) {
            this.GetEmployeeSwapShiftsList();
            this.toastr.success(
              this.translate.instant('EXCHANGE REQUEST ACCEPTED'),
              this.translate.instant('EXCHANGE REQUEST CONFIRMATION')
            );
          }
        })
      );
  }

  /**
   * Gibt die eigene Schicht zum Tausch frei — `POST /api/ShiftSwap/RequestSwap`.
   *
   * **Das ist die erste Stufe des Schichttauschs, und sie war tot.** Der alte Pfad
   * `PUT /ShiftSwap/PutShiftForSwap` existiert seit dem .NET-10-Umbau nicht mehr; weil ein 404
   * ohne `notifications` vom `HttpService` unterdrückt wird, klickte der Mitarbeiter
   * „Zum Tausch anbieten" und es passierte sichtbar **nichts**. Damit entstand nie ein
   * Tauschvorgang — und die Genehmigungsliste des Managers blieb zwangsläufig leer.
   *
   * Migriert am 31.08.2026. Möglich wurde das erst durch ein neues Feld im Backend: Die neue API
   * verlangt die **numerische** `WorkShiftId`, der Kalender lieferte aber nur `eventId` (`Guid`)
   * — und das Feld `id` im Kalender-Event trägt aus historischen Gründen die *Mitarbeiter*-Id
   * (`GetEventsForCompanyQueryHandler`: `Id = ws.EmployeeId`). `CalenderEvent.WorkShiftId` liefert
   * sie jetzt mit.
   *
   * `RequestType.Drop` heißt: „Ich gebe diese Schicht ab, wer übernimmt sie?" — ohne festes
   * Gegenüber. Der Direkttausch mit einer bestimmten Person bleibt offen, weil seine
   * Auswahlliste (`GetPotentialShiftSwappersList`) im neuen Backend kein Gegenstück hat.
   *
   * @param workShiftId Numerische Schicht-Id (**nicht** die `EventId`).
   * @param notes Optionale Begründung; sie erscheint beim Kollegen und beim Manager.
   */
  RequestShiftDrop(workShiftId: number, notes?: string): Observable<any> {
    return this.httpClient
      .requestCall(ApiEndPoints.RequestSwap, ApiMethod.POST, {
        WorkShiftId: workShiftId,
        RequestType: ShiftSwapRequestType.Drop,
        RequestNotes: notes?.trim() || '',
      })
      .pipe(
        tap((result: any) => {
          if (this.isSuccess(result)) {
            this.toastr.success(
              this.translate.instant('OPEN_OFFER_PUBLISHED_SUCCESS'),
              this.translate.instant('OFFER_PUBLISHED')
            );
            this.GetEmployeeSwapShiftsList();
          } else {
            // Der Server lehnt ab, wenn für diese Schicht schon ein offener Vorgang läuft
            // (`RequestSwapAsync` sperrt Duplikate) oder die Schicht nicht dem Anfragenden
            // gehört.
            this.toastr.error(
              this.translate.instant('SWAP_REQUEST_ALREADY_EXISTS'),
              this.translate.instant('ERROR')
            );
          }
        })
      );
  }

  /**
   * Lehnt eine an mich gerichtete Tauschanfrage ab — `POST /api/ShiftSwap/Reject/{id}`.
   *
   * Ergänzt am 31.08.2026. Vorher stand an der Aufrufstelle
   * (`SwapStatusService.respondToSwapRequest`) ein „TODO: Implement reject API call when
   * available" und es ging **kein** HTTP-Aufruf hinaus — der Angefragte bekam nur den Toast
   * `REJECT_FUNCTIONALITY_NOT_IMPLEMENTED`, und die Anfrage blieb offen stehen.
   *
   * Bewusst **nicht** über {@link CancelShiftSwapRequest} gelöst, obwohl beide dieselbe Route
   * treffen: Dort ist die Begründung fest `'Cancelled by requester'` und der Erfolgstext meldet
   * „zurückgezogen". Für eine Ablehnung durch den Angefragten wäre beides falsch — und die
   * Begründung landet serverseitig in `ResponseNotes` (`ShiftSwapService.RejectSwapAsync`),
   * wird also dem Antragsteller angezeigt.
   *
   * Dass derselbe Endpunkt beide Fälle bedient, ist Absicht des Backends: `RejectSwapAsync`
   * prüft nur den Status (`Created` oder `AcceptedFromEmployee`) und nicht, *wer* ablehnt.
   */
  RejectShiftSwapRequest(
    shiftSwapId: number,
    rejectionReason?: string
  ): Observable<any> {
    const reason =
      rejectionReason?.trim() ||
      this.translate.instant('SWAP_REJECTED_WITHOUT_REASON');

    return this.httpClient
      .requestCall(ApiEndPoints.RejectSwap + '/' + shiftSwapId, ApiMethod.POST, {
        ShiftSwapId: shiftSwapId,
        RejectionReason: reason,
      })
      .pipe(
        tap((result: any) => {
          if (this.isSuccess(result)) {
            this.toastr.success(
              this.translate.instant('SWAP_REQUEST_REJECTED_SUCCESS'),
              this.translate.instant('SWAP_REQUEST_REJECTED')
            );
            // Listen aktualisieren: die abgelehnte Anfrage verschwindet aus den offenen.
            this.GetEmployeeSwapShiftsList();
          } else {
            // Der Server lehnt ab, wenn der Vorgang den Zustand bereits verlassen hat
            // (z. B. abgelaufen oder vom Manager entschieden).
            this.toastr.error(
              this.translate.instant('SWAP_REQUEST_CANNOT_BE_REJECTED'),
              this.translate.instant('ERROR')
            );
          }
        })
      );
  }

  /**
   * Zieht die eigene Tauschanfrage zurück — `POST /api/ShiftSwap/Reject/{id}`.
   *
   * Migriert am 31.08.2026 (Phase 3). Das im alten Kommentar notierte Risiko ist geprüft:
   * `ShiftSwapService.RejectSwapAsync` verlangt lediglich den Status `Created` oder
   * `AcceptedFromEmployee` und schaut nicht darauf, *wer* ablehnt — ein Rückzug durch den
   * Initiator wird also angenommen. Ein eigener Cancel-Endpunkt ist dafür nicht nötig.
   *
   * Fachlich bleibt ein Unterschied: Der Vorgang endet im Status `Rejected`, nicht `Cancelled`.
   * Für den Nutzer ist beides „zurückgezogen"; wer die beiden Fälle in Auswertungen trennen
   * will, braucht einen eigenen Endpunkt.
   */
  CancelShiftSwapRequest(shiftSwapId: number): Observable<any> {
    return this.httpClient
      .requestCall(ApiEndPoints.RejectSwap + '/' + shiftSwapId, ApiMethod.POST, {
        ShiftSwapId: shiftSwapId,
        RejectionReason: 'Cancelled by requester',
      })
      .pipe(
        tap((result: any) => {
          if (this.isSuccess(result)) {
            this.toastr.success(
              this.translate.instant('SWAP_REQUEST_CANCELLED_SUCCESS'),
              this.translate.instant('REQUEST_CANCELLED')
            );
            // Listen aktualisieren
            this.GetEmployeeSwapShiftsList();
          } else {
            // Der Server lehnt den Rückzug ab, wenn der Vorgang den Zustand bereits
            // verlassen hat (z. B. vom Manager entschieden). Kein Unterscheiden mehr
            // zwischen -1 und „sonst": die neue API kennt diese Zahlencodes nicht.
            this.toastr.error(
              this.translate.instant('SWAP_REQUEST_CANNOT_BE_CANCELLED'),
              this.translate.instant('ERROR')
            );
          }
        })
      );
  }

  /**
   * @deprecated DEAD-ENDPOINT (Phase 4 — Marketplace-Feature entfernt).
   * Auswahl eines Kandidaten aus einer Open-Swap-Kandidatenliste — Konzept
   * existiert im neuen Backend nicht mehr. Aufrufende UI entfernen oder
   * Backend-Reimplementierung als separates Feature.
   */
  SelectCandidateForOpenSwap(
    shiftSwapId: number,
    selectedCandidateEmployeeId: number,
    selectedShiftId: number
  ): Observable<number> {
    return this.httpClient
      .requestCall(ApiEndPoints.SelectCandidateForOpenSwap, ApiMethod.POST, {
        ShiftSwapId: shiftSwapId,
        SelectedCandidateEmployeeId: selectedCandidateEmployeeId,
        SelectedShiftId: selectedShiftId,
      })
      .pipe(
        tap((result: number) => {
          if (result === 1) {
            this.toastr.success(
              this.translate.instant('CANDIDATE_SELECTED_SUCCESSFULLY'),
              this.translate.instant('SUCCESS')
            );
            // Listen aktualisieren
            this.GetEmployeeSwapShiftsList();
          } else {
            this.toastr.error(
              this.translate.instant('ERROR_SELECTING_CANDIDATE'),
              this.translate.instant('ERROR')
            );
          }
        })
      );
  }
}
